import { PROVIDER_OBLIGATION_CATALOGUE } from './catalogue';
import type {
  ObligationCatalogueRow,
  ObligationResolution,
  ProviderProfile,
  ResolveProviderObligationsOptions,
} from './types';

const unknown = (row: ObligationCatalogueRow, code: string): ObligationResolution => ({
  obligationId: row.id,
  status: 'UNKNOWN_SHOW_ANYWAY',
  reasonCodes: [code],
  citation: row.citation,
  scope: row.scope,
});
const result = (
  row: ObligationCatalogueRow,
  status: ObligationResolution['status'],
  reasonCodes: string[],
): ObligationResolution => ({
  obligationId: row.id,
  status,
  reasonCodes,
  citation: row.citation,
  scope: row.scope,
});

/** Every registration category value this catalogue knows how to reason about. */
const KNOWN_REGISTRATION_CATEGORIES = new Set(['1', '2', '3', '4', '5', '6', 'SUBSCRIPTION_TRIAL']);

/**
 * The five r 167-55(2A)-listed subscription service types. A subscription-
 * trial registration category alone is not positive evidence of any T1
 * duty that depends on a listed service; the declared service itself is.
 */
const LISTED_SUBSCRIPTION_SERVICE_TYPES = [
  'SUBSCRIPTION_ALLIED_HEALTH_THERAPY',
  'SUBSCRIPTION_HOME_COMMUNITY_RESPITE',
  'SUBSCRIPTION_PERSONAL_CARE',
  'SUBSCRIPTION_THERAPEUTIC_SERVICES_INDEPENDENT_LIVING',
  'SUBSCRIPTION_NURSING_CARE',
] as const;

const confirmed = (p: ProviderProfile) =>
  Boolean(p.profileConfirmedAt) && p.profileNeedsProviderConfirmation !== true;

type CategoryCheck = { applies: boolean | null; reasonCode: string };

/**
 * A declared category set only proves a positive match, or a confident
 * negative exclusion, when it is non-empty AND every member is a category
 * this catalogue recognises. An empty/undeclared set (registrationCategories
 * null, undefined, or []) can never rule an obligation out — the governing
 * rule is that DOES_NOT_APPLY requires positive evidence, and "no categories
 * declared" is the opposite of evidence. A set carrying an unrecognised value remains unknown until the provider
 * confirms a valid category set.
 */
function checkCategories(p: ProviderProfile, values: readonly string[]): CategoryCheck {
  const raw = p.registrationCategories;
  if (!raw || raw.length === 0) return { applies: null, reasonCode: 'CATEGORIES_NOT_DECLARED' };
  const set = new Set(raw);
  if (raw.some((c) => !KNOWN_REGISTRATION_CATEGORIES.has(c)))
    return { applies: null, reasonCode: 'CATEGORY_UNRECOGNISED' };
  if (values.some((v) => set.has(v as never)))
    return { applies: true, reasonCode: 'CATEGORY_MATCH' };
  return { applies: false, reasonCode: 'POSITIVE_CATEGORY_NON_MATCH' };
}

function evaluate(row: ObligationCatalogueRow, p: ProviderProfile): ObligationResolution {
  if (row.verification === 'UNVERIFIED') return unknown(row, 'LEGAL_BASIS_UNVERIFIED');
  if (row.rule === 'UNIVERSAL')
    return result(row, 'APPLIES', ['UNIVERSAL_REGISTERED_PROVIDER_DUTY']);
  if (row.rule === 'NDIS') {
    const state = p.ndisRegistrationFact?.state;
    return !state || state === 'UNKNOWN'
      ? unknown(row, 'NDIS_REGISTRATION_UNKNOWN')
      : result(row, state === 'REGISTERED' ? 'APPLIES' : 'DOES_NOT_APPLY', [
          state === 'REGISTERED' ? 'NDIS_REGISTERED' : 'POSITIVE_NDIS_NON_MATCH',
        ]);
  }
  if (!confirmed(p)) return unknown(row, 'PROFILE_UNCONFIRMED');
  if (
    row.id.startsWith('quality_standard') &&
    p.registrationCategories?.includes('SUBSCRIPTION_TRIAL')
  )
    return unknown(row, 'SUBSCRIPTION_TRIAL_NOT_A_REGISTRATION_CATEGORY');
  const categoryResult = (values: readonly string[]) => {
    const c = checkCategories(p, values);
    return c.applies === null
      ? unknown(row, c.reasonCode)
      : result(row, c.applies ? 'APPLIES' : 'DOES_NOT_APPLY', [c.reasonCode]);
  };
  // A subscription trial is a commercial state, not an Aged Care Act
  // registration category. It cannot establish either applicability or a
  // confident negative for the Quality Standards duties.
  const standardsCategoryResult = (values: readonly string[]) => {
    if (
      p.registrationCategories?.length === 1 &&
      p.registrationCategories[0] === 'SUBSCRIPTION_TRIAL'
    )
      return unknown(row, 'SUBSCRIPTION_TRIAL_NOT_A_REGISTRATION_CATEGORY');
    return categoryResult(values);
  };
  switch (row.rule) {
    case 'INCIDENT_MANAGEMENT':
      return categoryResult(['1', '3', '4', '5', '6', 'SUBSCRIPTION_TRIAL']);
    case 'CATEGORIES_123':
      return categoryResult(['1', '2', '3', 'SUBSCRIPTION_TRIAL']);
    case 'PRIVACY_APP_ENTITY':
      return unknown(row, 'HEALTH_SERVICE_SCOPE_UNVERIFIED');
    case 'CATEGORIES_456':
      return standardsCategoryResult(['4', '5', '6']);
    case 'CATEGORIES_56':
      return standardsCategoryResult(['5', '6']);
    case 'CATEGORY6':
      return standardsCategoryResult(['6']);
    case 'CATEGORY4_CARE_MANAGEMENT': {
      const standardsCategories = checkCategories(p, ['4', '5', '6']);
      if (standardsCategories.applies === null) return unknown(row, standardsCategories.reasonCode);
      if (p.registrationCategories?.some((category) => category === '5' || category === '6'))
        return result(row, 'APPLIES', ['CATEGORY_MATCH']);
      if (!standardsCategories.applies)
        return result(row, 'DOES_NOT_APPLY', [standardsCategories.reasonCode]);
      if (!p.declaredServiceTypes) return unknown(row, 'SERVICE_DECLARATION_UNKNOWN');
      const intents = ['CARE_MANAGEMENT', 'RESTORATIVE_CARE_MANAGEMENT'].map(
        (k) => p.declaredServiceTypes?.[k],
      );
      if (intents.some((x) => x === null)) return unknown(row, 'SERVICE_DECLARATION_UNKNOWN');
      return intents.some((x) => x === 'DELIVERS' || x === 'INTENDS_TO_DELIVER')
        ? result(row, 'APPLIES_MODIFIED', ['CATEGORY_4_CARE_MANAGEMENT_DECLARED'])
        : result(row, 'DOES_NOT_APPLY', ['POSITIVE_SERVICE_NON_MATCH']);
    }
    case 'GOVERNANCE_COMPOSITION': {
      const base = checkCategories(p, ['5', '6']);
      if (base.applies === null || p.entityType == null)
        return unknown(row, base.applies === null ? base.reasonCode : 'ENTITY_TYPE_UNKNOWN');
      if (!base.applies) return result(row, 'DOES_NOT_APPLY', [base.reasonCode]);
      if (p.entityType === 'GOVERNMENT_ENTITY' || p.entityType === 'LOCAL_GOVERNMENT')
        return result(row, 'APPLIES_MODIFIED', ['GOVERNMENT_ENTITY_SWITCH']);
      if (p.entityType === 'ACCO') return result(row, 'DOES_NOT_APPLY', ['ACCO_EXCEPTION']);
      if (p.entityType === 'CO_OPERATIVE') {
        if (p.coOperativeQualifyingRules == null)
          return unknown(row, 'COOPERATIVE_QUALIFICATION_UNKNOWN');
        return p.coOperativeQualifyingRules
          ? result(row, 'DOES_NOT_APPLY', ['QUALIFYING_COOPERATIVE_EXCEPTION'])
          : result(row, 'APPLIES', ['CATEGORY_MATCH']);
      }
      return result(row, 'APPLIES', ['CATEGORY_MATCH']);
    }
    case 'GOVERNANCE_CLINICAL_MEMBER': {
      const base = checkCategories(p, ['5', '6']);
      if (base.applies === null || p.entityType == null)
        return unknown(row, base.applies === null ? base.reasonCode : 'ENTITY_TYPE_UNKNOWN');
      if (!base.applies || p.entityType === 'ACCO')
        return result(row, 'DOES_NOT_APPLY', [!base.applies ? base.reasonCode : 'ACCO_EXCEPTION']);
      if (p.entityType === 'GOVERNMENT_ENTITY' || p.entityType === 'LOCAL_GOVERNMENT')
        return result(row, 'APPLIES_MODIFIED', ['GOVERNMENT_ENTITY_SWITCH']);
      return result(row, 'APPLIES', ['CATEGORY_MATCH']);
    }
    case 'COOP_TRAINING': {
      const base = checkCategories(p, ['5', '6']);
      if (base.applies === null || p.entityType == null || p.coOperativeQualifyingRules == null)
        return unknown(
          row,
          base.applies === null ? base.reasonCode : 'COOPERATIVE_QUALIFICATION_UNKNOWN',
        );
      const qualifies =
        base.applies && p.entityType === 'CO_OPERATIVE' && p.coOperativeQualifyingRules;
      return result(
        row,
        qualifies ? 'APPLIES_MODIFIED' : 'DOES_NOT_APPLY',
        qualifies ? ['QUALIFYING_COOPERATIVE'] : ['POSITIVE_ENTITY_OR_CATEGORY_NON_MATCH'],
      );
    }
    case 'ASSOCIATED_PROVIDER_NOTIFICATION': {
      // r 167-55(1) + item 8/8A of r 167-10: categories 4/5/6 carry the
      // notification duty unconditionally (subject to the labour-hire /
      // management-support carve-outs, which are arrangement-level facts
      // this resolver does not model). r 167-55(2A) confines the
      // subscription-trial limb positively to arrangements for delivery of
      // one of the five listed subscription service types, so a bare
      // SUBSCRIPTION_TRIAL registration category is never, by itself,
      // positive evidence — the declared service is.
      const core = checkCategories(p, ['4', '5', '6']);
      if (core.applies === true) return result(row, 'APPLIES', [core.reasonCode]);
      const subscription = checkCategories(p, ['SUBSCRIPTION_TRIAL']);
      if (core.applies === null || subscription.applies === null)
        return unknown(row, core.applies === null ? core.reasonCode : subscription.reasonCode);
      if (!subscription.applies) return result(row, 'DOES_NOT_APPLY', [core.reasonCode]);
      if (!p.declaredServiceTypes) return unknown(row, 'SUBSCRIPTION_SERVICE_DECLARATION_UNKNOWN');
      const intents = LISTED_SUBSCRIPTION_SERVICE_TYPES.map((k) => p.declaredServiceTypes?.[k]);
      if (intents.some((x) => x === null))
        return unknown(row, 'SUBSCRIPTION_SERVICE_DECLARATION_UNKNOWN');
      return intents.some((x) => x === 'DELIVERS' || x === 'INTENDS_TO_DELIVER')
        ? result(row, 'APPLIES_MODIFIED', ['LISTED_SUBSCRIPTION_SERVICE_DECLARED'])
        : result(row, 'DOES_NOT_APPLY', ['NO_LISTED_SUBSCRIPTION_SERVICE_DECLARED']);
    }
    case 'CHSP':
      return p.specialistProgrammes == null
        ? unknown(row, 'SPECIALIST_PROGRAMME_UNKNOWN')
        : result(row, p.specialistProgrammes.includes('CHSP') ? 'APPLIES' : 'DOES_NOT_APPLY', [
            p.specialistProgrammes.includes('CHSP')
              ? 'CHSP_DECLARED'
              : 'POSITIVE_PROGRAMME_NON_MATCH',
          ]);
    case 'JURISDICTIONAL':
      return !p.operatingJurisdictions?.length ||
        p.operatingJurisdictions.some((j) => !['QLD', 'VIC'].includes(j))
        ? unknown(row, 'OPERATING_JURISDICTION_UNKNOWN')
        : result(row, 'APPLIES', ['JURISDICTIONAL_DUTY']);
    default:
      return unknown(row, 'LEGAL_BASIS_UNVERIFIED');
  }
}

/** Pure, deterministic union resolver. Size band is intentionally not consulted in T1. */
export function resolveProviderObligations(
  profile: ProviderProfile,
  _options: ResolveProviderObligationsOptions = {},
): ObligationResolution[] {
  return [...PROVIDER_OBLIGATION_CATALOGUE]
    .sort((a, b) => a.id.localeCompare(b.id))
    .map((row) => {
      const resolved = evaluate(row, profile);
      const association = profile.associatedProvider;
      if (!association || !row.headProviderDuty) return resolved;
      if (association.activeHeadProviderIds.length === 0)
        return unknown(row, 'ASSOCIATED_HEAD_PROVIDER_UNKNOWN');
      if (association.activeHeadProviderIds.length > 1 && !association.selectedHeadProviderId)
        return unknown(row, 'MULTIPLE_HEAD_PROVIDERS_UNSELECTED');
      return result(row, 'VIA_REGISTERED_PROVIDER', [
        'ASSOCIATED_PROVIDER_READ_ONLY_APPLICABILITY',
        `HEAD_PROVIDER:${association.selectedHeadProviderId ?? association.activeHeadProviderIds[0]}`,
      ]);
    });
}
